import dayjs from '@/lib/dayjs'
import { normalizeNullableId } from '@/lib/services/occasions/occasions.utils'
import { serializeHeroValues } from './heroValues'

export const FEATURED_COLLECTION_BLOCK_TYPE = 'featuredCollection'
export const LEGACY_FEATURED_COLLECTION_BLOCK_TYPE = 'featuredCollections'
export const FEATURED_PRODUCT_BLOCK_TYPE = 'featuredProducts'
export const GALLERY_WIDGET_BLOCK_TYPE = 'galleryWidget'
export const CONTACT_US_WIDGET_BLOCK_TYPE = 'contactUsWidget'
export const CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE = 'customerReviews'
export const CUSTOMER_REVIEWS_PAGE_BLOCK_TYPE = 'customerReviewsPage'
export const DEFAULT_CONTACT_US_WIDGET_TITLE = 'Love what you see?'
export const DEFAULT_CONTACT_US_WIDGET_MESSAGE =
  "Tell us what you're planning and our floral team can help with custom designs, event flowers, and everyday arrangements."
export const DEFAULT_CONTACT_US_WIDGET_BUTTON_TEXT = 'Contact Us'
export const DEFAULT_REVIEWS_WIDGET_TITLE = 'Customer Reviews'
export const DEFAULT_REVIEWS_WIDGET_MESSAGE =
  'A few kind words from customers who trusted us with their flowers.'
export const CUSTOMER_REVIEWS_FORM_PLACEMENT_INLINE = 'inline'
export const CUSTOMER_REVIEWS_FORM_PLACEMENT_MODAL = 'modal'
export const FEATURED_CONTENT_WIDGET_TYPES = [
  FEATURED_COLLECTION_BLOCK_TYPE,
  LEGACY_FEATURED_COLLECTION_BLOCK_TYPE,
  FEATURED_PRODUCT_BLOCK_TYPE,
  GALLERY_WIDGET_BLOCK_TYPE,
  CONTACT_US_WIDGET_BLOCK_TYPE,
  CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE,
]
export const FEATURED_CONTENT_WIDGET_TYPE_SET = new Set(FEATURED_CONTENT_WIDGET_TYPES)
export const NO_OCCASION_OPTION_ID = 'none'

export const normalizeCustomerReviewsFormPlacement = (value) =>
  value === CUSTOMER_REVIEWS_FORM_PLACEMENT_MODAL
    ? CUSTOMER_REVIEWS_FORM_PLACEMENT_MODAL
    : CUSTOMER_REVIEWS_FORM_PLACEMENT_INLINE

export function getSeasonalReferenceHeroBlock(design, storedHeroBlock = {}) {
  const revision =
    design?.seasonal?.hero_mode === 'reference'
      ? design?.seasonal?.seasonal_design?.published_revision
      : null

  if (!revision) {
    return storedHeroBlock
  }

  const media = revision.media ?? null

  return {
    ...storedHeroBlock,
    props: {
      ...(storedHeroBlock?.props ?? {}),
      ...(revision.hero ?? {}),
    },
    media: media ? [media] : [],
  }
}

export const DEFAULT_SECTION_OVERRIDES = {
  header_banner: false,
  header_brand: false,
  header_nav_menu: false,
  hero: false,
  featured_content: false,
}
const NON_HOME_FEATURED_CONTENT_PAGE_SLUGS = ['/about-us', '/gallery', '/contact-us', '/reviews']
const PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_ALL = 'all'
const PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_INCLUDE = 'include'

export const DEFAULT_SCHEDULE = {
  is_enabled: false,
  timing_mode: 'custom',
  seasonal_schedule_profile_id: null,
  is_yearly: false,
  start_type: 'holiday_offset',
  starts_on: dayjs().format('YYYY-MM-DD'),
  start_holiday: 'valentines_day',
  start_occasion_id: null,
  start_offset_days: 21,
  end_type: 'holiday_offset',
  ends_on: null,
  end_holiday: 'valentines_day',
  end_occasion_id: null,
  end_offset_days: 7,
  end_days_after_start: 14,
  end_matches_start_holiday: true,
}

export const normalizeOffsetForForm = (value, fallbackValue) => {
  if (value === null || value === undefined || value === '') {
    return fallbackValue
  }

  const amount = Number(value)
  return Number.isFinite(amount) ? Math.abs(amount) : fallbackValue
}

const normalizePositiveOffsetForSubmit = (value) => {
  if (value === null || value === undefined || value === '') {
    return Number.NaN
  }

  const cleanedValue =
    typeof value === 'number' ? String(value) : String(value).replace(/[^0-9.-]/g, '')
  const amount = Number.parseInt(cleanedValue, 10)

  return Number.isInteger(amount) ? Math.abs(amount) : Number.NaN
}

const normalizeProductTypeKeysForSubmit = (value) => {
  if (!Array.isArray(value)) {
    return []
  }

  return [
    ...new Set(
      value.map((key) => (typeof key === 'string' ? key.trim() : '')).filter((key) => key !== '')
    ),
  ]
}

const normalizeHolidayOffsetsForSubmit = (schedule) => {
  if (!schedule || typeof schedule !== 'object') {
    return schedule
  }

  const startOffsetDays = normalizePositiveOffsetForSubmit(schedule.start_offset_days)
  const endOffsetDays = normalizePositiveOffsetForSubmit(schedule.end_offset_days)
  const normalizedSchedule = { ...schedule }
  const hasScheduleField = (field) =>
    Object.prototype.hasOwnProperty.call(normalizedSchedule, field)

  if (schedule.start_type === 'holiday_offset') {
    normalizedSchedule.end_type = 'holiday_offset'
  }

  if (schedule.start_type !== 'holiday_offset') {
    if (hasScheduleField('start_holiday')) {
      normalizedSchedule.start_holiday = null
    }
    if (hasScheduleField('start_occasion_id')) {
      normalizedSchedule.start_occasion_id = null
    }
    if (hasScheduleField('start_offset_days')) {
      normalizedSchedule.start_offset_days = null
    }
  } else {
    normalizedSchedule.start_occasion_id = normalizeNullableId(schedule.start_occasion_id)
  }

  if (schedule.start_type !== 'fixed_date') {
    if (hasScheduleField('starts_on')) {
      normalizedSchedule.starts_on = null
    }
    if (hasScheduleField('is_yearly')) {
      normalizedSchedule.is_yearly = false
    }
  }

  if (schedule.start_type === 'holiday_offset' && Number.isFinite(startOffsetDays)) {
    normalizedSchedule.start_offset_days = startOffsetDays === 0 ? 0 : -startOffsetDays
  }

  if (
    schedule.start_type === 'holiday_offset' &&
    normalizedSchedule.end_type === 'holiday_offset'
  ) {
    normalizedSchedule.end_holiday = normalizedSchedule.start_holiday
    normalizedSchedule.end_occasion_id = normalizedSchedule.start_occasion_id
  }

  if (normalizedSchedule.end_type !== 'holiday_offset') {
    if (hasScheduleField('end_holiday')) {
      normalizedSchedule.end_holiday = null
    }
    if (hasScheduleField('end_occasion_id')) {
      normalizedSchedule.end_occasion_id = null
    }
    if (hasScheduleField('end_offset_days')) {
      normalizedSchedule.end_offset_days = null
    }
  } else {
    normalizedSchedule.end_occasion_id = normalizeNullableId(normalizedSchedule.end_occasion_id)
  }

  if (normalizedSchedule.end_type !== 'fixed_date' && hasScheduleField('ends_on')) {
    normalizedSchedule.ends_on = null
  }

  if (
    normalizedSchedule.end_type !== 'days_after_start' &&
    hasScheduleField('end_days_after_start')
  ) {
    normalizedSchedule.end_days_after_start = null
  }

  if (normalizedSchedule.end_type === 'holiday_offset' && Number.isFinite(endOffsetDays)) {
    normalizedSchedule.end_offset_days = endOffsetDays
  }

  delete normalizedSchedule.end_matches_start_holiday

  return normalizedSchedule
}

const normalizeProductSettingsForSubmit = (value) => {
  const productsPage = value?.pages?.['/products']
  const productSettingsBlock = productsPage?.blocks?.main?.[0]
  const props = productSettingsBlock?.props

  if (!props || typeof props !== 'object') {
    return value
  }

  const nextProps = { ...props }
  const includedProductTypeKeys = normalizeProductTypeKeysForSubmit(props.includedProductTypeKeys)
  const hasProductTypeScope =
    Object.prototype.hasOwnProperty.call(props, 'productTypeScope') &&
    props.productTypeScope != null

  if (hasProductTypeScope) {
    const shouldUseProductTypeIncludes =
      props.productTypeScope === PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_INCLUDE

    nextProps.productTypeScope = shouldUseProductTypeIncludes
      ? PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_INCLUDE
      : PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_ALL
    nextProps.includedProductTypeKeys = shouldUseProductTypeIncludes ? includedProductTypeKeys : []

    delete nextProps.categoryScope
    delete nextProps.includedCategoryIds
    delete nextProps.excludedCategoryIds
  } else if (includedProductTypeKeys.length > 0) {
    nextProps.productTypeScope = PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_INCLUDE
    nextProps.includedProductTypeKeys = includedProductTypeKeys
  } else {
    nextProps.productTypeScope = PRODUCT_DISCLAIMER_PRODUCT_TYPE_SCOPE_ALL
    nextProps.includedProductTypeKeys = []
  }

  delete nextProps.categoryScope
  delete nextProps.includedCategoryIds
  delete nextProps.excludedCategoryIds

  return {
    ...value,
    pages: {
      ...value.pages,
      '/products': {
        ...productsPage,
        blocks: {
          ...productsPage.blocks,
          main: [
            {
              ...productSettingsBlock,
              props: nextProps,
            },
            ...productsPage.blocks.main.slice(1),
          ],
        },
      },
    },
  }
}

export const normalizeSchedule = (schedule) => {
  const source = schedule && typeof schedule === 'object' ? schedule : {}
  const startType = source?.start_type ?? DEFAULT_SCHEDULE.start_type
  const startHoliday = source?.start_holiday ?? DEFAULT_SCHEDULE.start_holiday
  const endType =
    startType === 'holiday_offset'
      ? 'holiday_offset'
      : (source?.end_type ?? DEFAULT_SCHEDULE.end_type)
  const shouldMatchHolidayEnd = startType === 'holiday_offset' && endType === 'holiday_offset'
  const endHoliday = shouldMatchHolidayEnd
    ? startHoliday
    : (source?.end_holiday ?? source?.start_holiday ?? DEFAULT_SCHEDULE.end_holiday)
  const startOccasionId = normalizeNullableId(
    source?.start_occasion_id ?? source?.start_occasion?.id
  )

  return {
    ...DEFAULT_SCHEDULE,
    ...source,
    start_type: startType,
    end_type: endType,
    start_holiday: startHoliday,
    is_enabled: !!source?.is_enabled,
    is_yearly: startType === 'fixed_date' ? !!source?.is_yearly : false,
    start_occasion_id: startOccasionId,
    start_offset_days: normalizeOffsetForForm(
      source?.start_offset_days,
      DEFAULT_SCHEDULE.start_offset_days
    ),
    end_holiday: endHoliday,
    end_occasion_id: shouldMatchHolidayEnd
      ? startOccasionId
      : normalizeNullableId(source?.end_occasion_id ?? source?.end_occasion?.id),
    end_offset_days: normalizeOffsetForForm(
      source?.end_offset_days,
      DEFAULT_SCHEDULE.end_offset_days
    ),
    end_days_after_start:
      source?.end_days_after_start === null || source?.end_days_after_start === undefined
        ? DEFAULT_SCHEDULE.end_days_after_start
        : Number(source.end_days_after_start),
    end_matches_start_holiday: true,
  }
}

export const normalizeSectionOverrides = (design) => {
  const sourceOverrides = design?.section_overrides || {}

  return {
    ...DEFAULT_SECTION_OVERRIDES,
    ...sourceOverrides,
    header_banner: !!sourceOverrides.header_banner,
    header_brand: false,
    header_nav_menu: !!sourceOverrides.header_nav_menu,
  }
}

export const isSeasonalFeaturedCollectionBlock = (block) =>
  block?.type === FEATURED_COLLECTION_BLOCK_TYPE ||
  block?.type === LEGACY_FEATURED_COLLECTION_BLOCK_TYPE

export const getFeaturedCollectionBlockCollectionId = (block) => {
  const collectionRef = Array.isArray(block?.collections) ? block.collections[0] : null
  const rawId = collectionRef?.collection_id ?? collectionRef?.collection?.id ?? null
  const collectionId = Number(rawId)

  return Number.isFinite(collectionId) && collectionId > 0 ? collectionId : null
}

export const hasSeasonalFeaturedCollectionSelection = (block) =>
  isSeasonalFeaturedCollectionBlock(block) && getFeaturedCollectionBlockCollectionId(block) !== null

export const getPreferredSeasonalFeaturedCollectionBlock = (blocks) => {
  const seasonalBlocks = (Array.isArray(blocks) ? blocks : []).filter(
    isSeasonalFeaturedCollectionBlock
  )

  return seasonalBlocks.find(hasSeasonalFeaturedCollectionSelection) ?? seasonalBlocks[0] ?? null
}

export const getSelectedSeasonalFeaturedCollectionBlock = (blocks) =>
  (Array.isArray(blocks) ? blocks : []).find(hasSeasonalFeaturedCollectionSelection) ?? null

const normalizeNonDefaultOverridePayload = (value) => {
  if (value?.is_default) {
    return value
  }

  const pages = value?.pages

  if (!pages || typeof pages !== 'object') {
    return value
  }

  let nextPages = pages
  const setPageBlocks = (slug, page, blocks) => {
    if (nextPages === pages) {
      nextPages = { ...pages }
    }

    nextPages[slug] = {
      ...page,
      blocks,
    }
  }

  const homePage = pages['/']
  const homeBlocks = homePage?.blocks

  if (homeBlocks) {
    const nextBlocks = { ...homeBlocks }

    if (!value?.section_overrides?.header_nav_menu) {
      const headerBlocks = homeBlocks?.header
      const navigationBlocks = headerBlocks?.navMenu

      if (Array.isArray(navigationBlocks) && navigationBlocks[0]) {
        nextBlocks.header = {
          ...headerBlocks,
          navMenu: [
            {
              ...navigationBlocks[0],
              props: {
                ...(navigationBlocks[0]?.props ?? {}),
                itemsMode: 'automatic',
              },
              nav_items: [],
            },
            ...navigationBlocks.slice(1),
          ],
        }
      }
    }

    if (value?.section_overrides?.featured_content) {
      const mainBlocks = Array.isArray(homeBlocks?.main) ? homeBlocks.main : []
      const seasonalFeaturedCollection = getSelectedSeasonalFeaturedCollectionBlock(mainBlocks)
      nextBlocks.main = seasonalFeaturedCollection
        ? applyMainBlockSortOrder([seasonalFeaturedCollection])
        : []
    } else if (Object.prototype.hasOwnProperty.call(homeBlocks, 'main')) {
      delete nextBlocks.main
    }

    setPageBlocks('/', homePage, nextBlocks)
  }

  NON_HOME_FEATURED_CONTENT_PAGE_SLUGS.forEach((slug) => {
    const page = pages[slug]
    const mainBlocks = page?.blocks?.main

    if (!Array.isArray(mainBlocks)) {
      return
    }

    const nextMainBlocks = mainBlocks.filter((block) => !isFeaturedContentWidgetBlock(block))

    if (nextMainBlocks.length === mainBlocks.length) {
      return
    }

    setPageBlocks(slug, page, {
      ...page.blocks,
      main: applyMainBlockSortOrder(nextMainBlocks),
    })
  })

  if (nextPages === pages) {
    return value
  }

  return {
    ...value,
    pages: nextPages,
  }
}

const keepHeaderLogoEnabled = (value) => {
  const homePage = value?.pages?.['/']
  const brandBlocks = homePage?.blocks?.header?.brand
  if (!Array.isArray(brandBlocks) || !brandBlocks[0]) {
    return value
  }

  return {
    ...value,
    pages: {
      ...value.pages,
      '/': {
        ...homePage,
        blocks: {
          ...homePage.blocks,
          header: {
            ...homePage.blocks.header,
            brand: [
              {
                ...brandBlocks[0],
                is_enabled: true,
              },
              ...brandBlocks.slice(1),
            ],
          },
        },
      },
    },
  }
}

export const applySortOrder = (items = []) =>
  items.map((item, index) => ({ ...item, sort_order: index + 1 }))

export const applyMainBlockSortOrder = (items = []) =>
  items.map((item, index) => ({ ...item, sort_order: index }))

export const applyPageMainBlockSortOrder = (value) => {
  const pages = value?.pages

  if (!pages || typeof pages !== 'object') {
    return value
  }

  let hasMainBlocks = false
  const nextPages = Object.entries(pages).reduce((acc, [slug, page]) => {
    const mainBlocks = page?.blocks?.main

    if (!Array.isArray(mainBlocks)) {
      acc[slug] = page
      return acc
    }

    hasMainBlocks = true
    acc[slug] = {
      ...page,
      blocks: {
        ...page.blocks,
        main: applyMainBlockSortOrder(mainBlocks),
      },
    }

    return acc
  }, {})

  if (!hasMainBlocks) {
    return value
  }

  return {
    ...value,
    pages: nextPages,
  }
}

export const buildDesignSubmitPayload = ({ value }) => {
  const heroBlocks = value?.pages?.['/']?.blocks?.hero?.hero
  const heroBlock = Array.isArray(heroBlocks) ? heroBlocks[0] : null
  const payload = heroBlock?.props
    ? {
        ...value,
        pages: {
          ...value.pages,
          '/': {
            ...value.pages['/'],
            blocks: {
              ...value.pages['/'].blocks,
              hero: {
                ...value.pages['/'].blocks.hero,
                hero: [
                  { ...heroBlock, props: serializeHeroValues(heroBlock.props) },
                  ...heroBlocks.slice(1),
                ],
              },
            },
          },
        },
      }
    : { ...value }

  delete payload.store_settings
  delete payload.collection_visibility_updates

  if (Object.prototype.hasOwnProperty.call(payload, 'occasion_id')) {
    payload.occasion_id = normalizeNullableId(payload.occasion_id)
  }

  if (payload.seasonal) {
    payload.seasonal = {
      ...payload.seasonal,
      seasonal_tag_id: normalizeNullableId(
        payload.seasonal.seasonal_tag_id ?? payload.seasonal.occasion_id
      ),
    }
    delete payload.seasonal.occasion_id
  }

  delete payload.seasonal_tag_id

  if (Object.prototype.hasOwnProperty.call(payload, 'schedule')) {
    payload.schedule = normalizeHolidayOffsetsForSubmit(payload.schedule)
  }

  return keepHeaderLogoEnabled(
    normalizeProductSettingsForSubmit(
      normalizeNonDefaultOverridePayload(applyPageMainBlockSortOrder(payload))
    )
  )
}

export const getStorefrontProductCount = (collection) => {
  if (collection?.storefront_products_count !== undefined) {
    return Number(collection.storefront_products_count)
  }

  if (collection?.active_products_count !== undefined) {
    return Number(collection.active_products_count)
  }

  return null
}

export function applyCollectionVisibilityUpdates(collections = [], visibilityUpdates = []) {
  if (!Array.isArray(collections) || !Array.isArray(visibilityUpdates)) {
    return Array.isArray(collections) ? collections : []
  }

  const visibilityByCollectionId = new Map(
    visibilityUpdates
      .map((update) => [Number(update?.collection_id), update?.is_published])
      .filter(
        ([collectionId, isPublished]) =>
          Number.isFinite(collectionId) && typeof isPublished === 'boolean'
      )
  )

  if (visibilityByCollectionId.size === 0) {
    return collections
  }

  return collections.map((collection) => {
    const collectionId = Number(collection?.id)

    return visibilityByCollectionId.has(collectionId)
      ? { ...collection, is_published: visibilityByCollectionId.get(collectionId) }
      : collection
  })
}

export function updateCollectionVisibilityDraft(
  collections = [],
  visibilityUpdates = [],
  collectionId,
  isPublished
) {
  const normalizedCollectionId = Number(collectionId)
  const collection = collections.find(
    (candidate) => Number(candidate?.id) === normalizedCollectionId
  )

  if (!collection || typeof isPublished !== 'boolean') {
    return Array.isArray(visibilityUpdates) ? visibilityUpdates : []
  }

  const remainingUpdates = (Array.isArray(visibilityUpdates) ? visibilityUpdates : []).filter(
    (update) => Number(update?.collection_id) !== normalizedCollectionId
  )

  return (collection.is_published !== false) === isPublished
    ? remainingUpdates
    : [...remainingUpdates, { collection_id: normalizedCollectionId, is_published: isPublished }]
}

export const COLLECTION_STOREFRONT_VISIBILITY_ISSUE = Object.freeze({
  HIDDEN: 'hidden',
  NO_AVAILABLE_PRODUCTS: 'no_available_products',
})

export const getCollectionStorefrontVisibilityIssue = (collection) => {
  if (collection?.is_published === false) {
    return COLLECTION_STOREFRONT_VISIBILITY_ISSUE.HIDDEN
  }

  const storefrontProductCount = getStorefrontProductCount(collection)

  if (storefrontProductCount === null || Number.isNaN(storefrontProductCount)) {
    return null
  }

  return storefrontProductCount > 0
    ? null
    : COLLECTION_STOREFRONT_VISIBILITY_ISSUE.NO_AVAILABLE_PRODUCTS
}

export const isCollectionVisibleOnStorefront = (collection) =>
  getCollectionStorefrontVisibilityIssue(collection) === null

export const isCollectionHiddenOnStorefront = (collection) =>
  !isCollectionVisibleOnStorefront(collection)

const collectionHasMerchandisingTag = (collection, seasonalTagId) => {
  const normalizedSeasonalTagId = Number(seasonalTagId)

  if (!Number.isFinite(normalizedSeasonalTagId) || normalizedSeasonalTagId <= 0) {
    return false
  }

  const occasionIds = Array.isArray(collection?.occasion_ids)
    ? collection.occasion_ids
    : Array.isArray(collection?.occasions)
      ? collection.occasions.map((occasion) => occasion?.id)
      : []
  const seasonIds = Array.isArray(collection?.season_ids)
    ? collection.season_ids
    : Array.isArray(collection?.seasons)
      ? collection.seasons.map((season) => season?.id)
      : []

  return [...occasionIds, ...seasonIds].some((id) => Number(id) === normalizedSeasonalTagId)
}

export const resolveAutomaticSeasonalFeaturedCollection = (collections = [], seasonalTagId) => {
  let bestMatch = null
  let bestProductCount = -1

  for (const collection of Array.isArray(collections) ? collections : []) {
    if (
      !isCollectionVisibleOnStorefront(collection) ||
      !collectionHasMerchandisingTag(collection, seasonalTagId)
    ) {
      continue
    }

    const productCount = getStorefrontProductCount(collection)
    const comparableProductCount = Number.isFinite(productCount) ? productCount : 0

    if (!bestMatch || comparableProductCount > bestProductCount) {
      bestMatch = collection
      bestProductCount = comparableProductCount
    }
  }

  return bestMatch
}

export const sortCollectionsByName = (collections = []) =>
  [...(Array.isArray(collections) ? collections : [])].sort((a, b) =>
    String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  )

export const buildStorefrontCollectionOptions = (collections = []) =>
  sortCollectionsByName(collections)
    .filter(isCollectionVisibleOnStorefront)
    .map((collection) => ({ id: collection.id?.toString(), name: collection.name }))

export const buildFeaturedCollectionOptions = (collections = []) =>
  sortCollectionsByName(collections).map((collection) => ({
    id: collection.id?.toString(),
    name: collection.name,
    disabled: isCollectionHiddenOnStorefront(collection),
  }))

export const isFeaturedContentWidgetBlock = (block) =>
  FEATURED_CONTENT_WIDGET_TYPE_SET.has(block?.type)
